import { useNavigate, useLocation } from 'react-router-dom';

// Persistent bottom tab bar for the five main screens: Home / Taxes / Cash flow
// / Expenses / You. Fixed to the viewport bottom; active tab is derived from the
// current route. Rendered by the shells of the main screens only (Dashboard's
// shell and the shared DetailScreen wrapper) — deep/task screens don't render it.
//
// Screens that show this bar must pad their scroll area by TAB_BAR_HEIGHT so the
// last content isn't hidden behind the fixed bar.
const BLUE  = 'var(--signal)';
const TEXT3 = 'var(--mute)';

// Rendered height of the bar, excluding the iOS safe-area inset padded below
// it. Measured, not nominal: there is no box-sizing reset in index.css, so each
// button's minHeight bounds its content box only and the 8px vertical padding
// adds on top — 60 + 8 + 8 = 76. Exported so screen shells can reserve matching
// bottom padding.
export const TAB_BAR_HEIGHT = 76;

// Content-box height of one tab button, which is what minHeight actually
// constrains. Deliberately not TAB_BAR_HEIGHT: that is the outer measurement,
// and feeding it back in here would stack the padding a second time and grow
// the bar to 92.
const TAB_CONTENT_HEIGHT = 60;

// Every icon sets stroke/fill through style rather than a presentation
// attribute, because var() does not resolve in a presentation attribute.
function HomeIcon({ color }) {
  // 2x2 grid; the leading diagonal reads solid, the other pair recedes.
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="8" height="8" rx="2.5" style={{ fill: color }} />
      <rect x="13" y="3" width="8" height="8" rx="2.5" style={{ fill: color, opacity: 0.4 }} />
      <rect x="3" y="13" width="8" height="8" rx="2.5" style={{ fill: color, opacity: 0.4 }} />
      <rect x="13" y="13" width="8" height="8" rx="2.5" style={{ fill: color }} />
    </svg>
  );
}

function TaxesIcon({ color }) {
  // Calendar: body, header rule, two binding ticks.
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: color }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2.5" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="6" />
      <line x1="16" y1="3" x2="16" y2="6" />
    </svg>
  );
}

function CashflowIcon({ color }) {
  // Four bars of varying height on a baseline.
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: color }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="14" x2="4" y2="18" />
      <line x1="9.3" y1="9" x2="9.3" y2="18" />
      <line x1="14.7" y1="11.5" x2="14.7" y2="18" />
      <line x1="20" y1="6" x2="20" y2="18" />
      <line x1="3" y1="21" x2="21" y2="21" />
    </svg>
  );
}

function ExpensesIcon({ color }) {
  // Rounded card with a rule near the top.
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: color }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <line x1="2.5" y1="9.5" x2="21.5" y2="9.5" />
    </svg>
  );
}

function YouIcon({ color }) {
  // Head above a shoulders arc.
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ stroke: color }} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

const TABS = [
  { label: 'Home',      route: '/dashboard', Icon: HomeIcon },
  { label: 'Taxes',     route: '/taxes',     Icon: TaxesIcon },
  { label: 'Cash flow', route: '/cashflow',  Icon: CashflowIcon },
  { label: 'Expenses',  route: '/expenses',  Icon: ExpensesIcon },
  { label: 'You',       route: '/you',       Icon: YouIcon },
];

export default function BottomTabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100,
      backgroundColor: 'var(--card)',
      borderTop: '0.5px solid rgba(0,0,0,0.12)',
      paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
    }}>
      {/* Inner column matches the app's 480px content width and centers on desktop */}
      <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex' }}>
        {TABS.map(({ label, route, Icon }) => {
          const active = pathname === route;
          const color = active ? BLUE : TEXT3;
          return (
            <button
              key={route}
              onClick={() => navigate(route)}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              style={{
                flex: 1,
                minHeight: TAB_CONTENT_HEIGHT, // ≥44px touch target
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 3,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '8px 0', color,
              }}
            >
              <Icon color={color} />
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 500, color }}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
